import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionThreeImagesEightTextsApi from "hooks/api/private/blocks/useSectionThreeImagesEightTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionThreeImagesEightTextsBody,
    SectionThreeImagesEightTextsDetails,
} from "types/api/blocks/sectionThreeImagesEightTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia, possibleEmptyMedia } from "utils/validators";

export type SectionThreeImagesEightTextsForm = {
    id: string | undefined;
    img_one_id: string | null;
    img_one: DesktopMobileForm;
    img_two_id: string | null;
    img_two: DesktopMobileForm;
    img_background_id: string | null;
    img_background: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title_1: string;
            text_1: string;
            button_label_1: string;
            button_url_1: string;
            title_2: string;
            text_2: string;
            button_label_2: string;
            button_url_2: string;
        };
    };
};

type SectionOneImageFiveTextsType = {
    setImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    setSecondImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setFirstImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setBackgroundImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    formHook: FormHookType<SectionThreeImagesEightTextsForm>;
};

function useSectionThreeImagesEightTexts(): SectionOneImageFiveTextsType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const {
        saveSectionThreeImagesEightTexts,
        getOneSectionThreeImagesEightTexts,
        deleteSectionThreeImagesEightTexts,
    } = useSectionThreeImagesEightTextsApi();

    const schema = React.useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                img_one: notEmptyMedia,
                img_two: notEmptyMedia,
                img_background: possibleEmptyMedia,
                img_one_id: {
                    type: "simple",
                    validators: [],
                },
                img_two_id: {
                    type: "simple",
                    validators: [],
                },
                img_background_id: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title_1: {
                                type: "simple",
                                validators: [],
                            },
                            text_1: {
                                type: "simple",
                                validators: [],
                            },
                            button_label_1: {
                                type: "simple",
                                validators: [],
                            },
                            button_url_1: {
                                type: "simple",
                                validators: [],
                            },
                            title_2: {
                                type: "simple",
                                validators: [],
                            },
                            text_2: {
                                type: "simple",
                                validators: [],
                            },
                            button_label_2: {
                                type: "simple",
                                validators: [],
                            },
                            button_url_2: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: SectionThreeImagesEightTextsForm = React.useMemo(() => {
        return {
            id: undefined,
            img_one_id: null,
            img_one: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            img_two_id: null,
            img_two: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            img_background_id: null,
            img_background: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title_1: "",
                    text_1: "",
                    button_label_1: "",
                    button_url_1: "",
                    title_2: "",
                    text_2: "",
                    button_label_2: "",
                    button_url_2: "",
                },
                en: {
                    title_1: "",
                    text_1: "",
                    button_label_1: "",
                    button_url_1: "",
                    title_2: "",
                    text_2: "",
                    button_label_2: "",
                    button_url_2: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionThreeImagesEightTextsForm>(
        schema,
        initialValues,
        getExtraErrorsDescription,
    );

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage: setImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [sectionThreeImagesEightTextsDetails, setSectionThreeImagesEightTextsDetails] =
        React.useState<SectionThreeImagesEightTextsDetails | undefined>(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionThreeImagesEightTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionThreeImagesEightTextsDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [
        setSectionThreeImagesEightTextsDetails,
        getOneSectionThreeImagesEightTexts,
        blockGroupCompositionId,
    ]);

    React.useEffect(() => {
        if (sectionThreeImagesEightTextsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionThreeImagesEightTextsDetails.id;
                newForm.text = sectionThreeImagesEightTextsDetails.text;

                newForm.img_one_id = sectionThreeImagesEightTextsDetails.img_one_id;

                if (sectionThreeImagesEightTextsDetails.img_one) {
                    newForm.img_one = {
                        id: sectionThreeImagesEightTextsDetails.img_one.id,
                        desktop: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_one.desktop.id,
                            label: "Desktop",
                            filename: sectionThreeImagesEightTextsDetails.img_one.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_one.mobile.id,
                            label: "Mobile",
                            filename: sectionThreeImagesEightTextsDetails.img_one.mobile.filename,
                            status: 2,
                        }),
                    };
                } else {
                    newForm.img_one = {
                        id: undefined,
                        desktop: createAttachmentType({ id: null, label: "Desktop" }),
                        mobile: createAttachmentType({ id: null, label: "Mobile" }),
                    };
                }

                newForm.img_two_id = sectionThreeImagesEightTextsDetails.img_two_id;

                if (sectionThreeImagesEightTextsDetails.img_two) {
                    newForm.img_two = {
                        id: sectionThreeImagesEightTextsDetails.img_two.id,
                        desktop: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_two.desktop.id,
                            label: "Desktop",
                            filename: sectionThreeImagesEightTextsDetails.img_two.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_two.mobile.id,
                            label: "Mobile",
                            filename: sectionThreeImagesEightTextsDetails.img_two.mobile.filename,
                            status: 2,
                        }),
                    };
                } else {
                    newForm.img_two = {
                        id: undefined,
                        desktop: createAttachmentType({ id: null, label: "Desktop" }),
                        mobile: createAttachmentType({ id: null, label: "Mobile" }),
                    };
                }

                newForm.img_background_id = sectionThreeImagesEightTextsDetails.img_background_id;

                if (sectionThreeImagesEightTextsDetails.img_background) {
                    newForm.img_background = {
                        id: sectionThreeImagesEightTextsDetails.img_background.id,
                        desktop: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_background.desktop.id,
                            label: "Desktop",
                            filename:
                                sectionThreeImagesEightTextsDetails.img_background.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionThreeImagesEightTextsDetails.img_background.mobile.id,
                            label: "Mobile",
                            filename:
                                sectionThreeImagesEightTextsDetails.img_background.mobile.filename,
                            status: 2,
                        }),
                    };
                } else {
                    newForm.img_background = {
                        id: undefined,
                        desktop: createAttachmentType({ id: null, label: "Desktop" }),
                        mobile: createAttachmentType({ id: null, label: "Mobile" }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, sectionThreeImagesEightTextsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const section3Images8TextsBody: SectionThreeImagesEightTextsBody = {
                    id: blockGroupCompositionId,
                    img_one: {
                        id: form.img_one.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_one.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_one.mobile),
                    },
                    img_two: {
                        id: form.img_two.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_two.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_two.mobile),
                    },
                    img_background: {
                        id: form.img_background.id,
                        desktop: converterAttachmentToFileWithPayload(form.img_background.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img_background.mobile),
                    },
                    text: {
                        it: {
                            title_1: form.text.it.title_1,
                            text_1: form.text.it.text_1,
                            button_label_1: form.text.it.button_label_1,
                            button_url_1: form.text.it.button_url_1,
                            title_2: form.text.it.title_2,
                            text_2: form.text.it.text_2,
                            button_label_2: form.text.it.button_label_2,
                            button_url_2: form.text.it.button_url_2,
                        },
                        en: {
                            title_1: form.text.en.title_1,
                            text_1: form.text.en.text_1,
                            button_label_1: form.text.en.button_label_1,
                            button_url_1: form.text.en.button_url_1,
                            title_2: form.text.en.title_2,
                            text_2: form.text.en.text_2,
                            button_label_2: form.text.en.button_label_2,
                            button_url_2: form.text.en.button_url_2,
                        },
                    },
                };

                const [apiCall] = saveSectionThreeImagesEightTexts(section3Images8TextsBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionThreeImagesEightTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    const { setDesktopMobileImage: setBackgroundImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_background",
    });

    const { setDesktopMobileImage: setFirstImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_one",
    });

    const { setDesktopMobileImage: setSecondImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img_two",
    });

    return React.useMemo(
        (): SectionOneImageFiveTextsType => ({
            buttonDelete: deleteSectionThreeImagesEightTexts,
            setBackgroundImage,
            setFirstImage,
            setSecondImage,
            setImage,
            formHook,
        }),
        [
            deleteSectionThreeImagesEightTexts,
            setBackgroundImage,
            setFirstImage,
            setSecondImage,
            setImage,
            formHook,
        ],
    );
}

export default useSectionThreeImagesEightTexts;
